import React, { useCallback, useRef, useState } from 'react';

export type FlowStepUtils = {
  setStep: SetStepFunction;
  skipStep: MoveStepFunction;
};

export type FlowStepProps = {
  nextStep: MoveStepFunction;
  previousStep: MoveStepFunction;
  setStep: SetStepFunction;
  totalSteps: number;
};

export interface StepComponent<
  StepNames extends string,
  TConfig extends Record<string, unknown> = Record<string, never>,
> {
  stepName: StepNames;
  component: React.FC<any>;
  props?: Record<string, unknown>;
  prerequisite?: PrerequisiteFunction;
  config?: TConfig;
}

export type SetStepFunction = (
  stepName: string,
  props?: Record<string, unknown>,
  direction?: StepTransitionDirection,
) => void;
export type MoveStepFunction = (props?: Record<string, unknown>) => void;
export type PrerequisiteFunction = (
  props: Record<string, unknown>,
  utils: FlowStepUtils,
) => Promise<Record<string, unknown> | void>;

export type StepTransitionDirection = 'forward' | 'backward';

export interface FlowStepContext<
  StepNames extends string = string,
  TConfig extends Record<string, unknown> = Record<string, never>,
> {
  stepName: StepNames;
  component: React.FC;
  direction?: StepTransitionDirection;
  config?: TConfig;
}

export function useFlowStep<
  StepNames extends string,
  TConfig extends Record<string, unknown> = Record<string, never>,
>(
  steps: StepComponent<StepNames, TConfig>[],
  initialStepName: string,
  options?: { onComplete?: () => void },
): {
  activeStepContext: FlowStepContext<StepNames, TConfig>;
  setStep: SetStepFunction;
  nextStep: MoveStepFunction;
  previousStep: MoveStepFunction;
  onComplete?: () => void;
} {
  const initialIndex = steps.findIndex((s) => s.stepName === initialStepName);
  if (initialIndex === -1) throw new Error(`Step "${initialStepName}" not found`);

  const [stepIndex, setStepIndex] = useState(initialIndex);
  const [stepProps, setStepProps] = useState(steps[initialIndex].props || {});

  const navigationRef = useRef(false);
  const directionRef = useRef<StepTransitionDirection>('forward');

  const setStepInternal = useCallback(
    async (stepName: string, props?: Record<string, unknown>, navDirection?: StepTransitionDirection) => {
      const targetIndex = steps.findIndex((s) => s.stepName === stepName);
      if (targetIndex === -1) throw new Error(`Step "${stepName}" not defined`);

      const mergedProps = { ...steps[targetIndex].props, ...(props || {}) };

      const effectiveDirection = navDirection || directionRef.current || 'forward';
      directionRef.current = effectiveDirection;

      const utils: FlowStepUtils = {
        setStep: (name, props, dir) => {
          navigationRef.current = true;
          directionRef.current = dir || 'forward';
          void setStepInternal(name, props, dir);
        },
        skipStep: (p) => {
          navigationRef.current = true;
          const dir = directionRef.current || 'forward';
          const nextIndex = dir === 'forward' ? targetIndex + 1 : targetIndex - 1;
          if (nextIndex < 0 || nextIndex >= steps.length) {
            options?.onComplete?.();
            return;
          }
          const nextDef = steps[nextIndex];
          navigationRef.current = false;
          void setStepInternal(nextDef.stepName, { ...nextDef.props, ...(p || {}) }, dir);
        },
      };

      const stepDef = steps[targetIndex];
      let newProps = { ...mergedProps };
      if (stepDef.prerequisite) {
        const result = await stepDef.prerequisite(mergedProps, utils);
        if (!result) {
          if (!navigationRef.current) {
            utils.skipStep();
          }
          return;
        }
        if (navigationRef.current) return;
        newProps = { ...newProps, ...(result || {}) };
      }

      if (navigationRef.current) return;

      setStepProps(newProps);
      setStepIndex(targetIndex);
    },
    [steps],
  );

  const setStep = useCallback(
    (stepName: string, props?: Record<string, unknown>, dir?: StepTransitionDirection) => {
      navigationRef.current = false;
      directionRef.current = dir || 'forward';
      return setStepInternal(stepName, props, dir);
    },
    [setStepInternal],
  );

  const nextStep = useCallback(
    (props?: Record<string, unknown>) => {
      const nextStepName = steps[stepIndex + 1]?.stepName;
      if (!nextStepName) {
        options?.onComplete?.();
        return;
      }
      void setStep(nextStepName, props, 'forward');
    },
    [stepIndex, steps, setStep, options],
  );

  const previousStep = useCallback(
    (props?: Record<string, unknown>) => {
      void setStep(steps[stepIndex - 1]?.stepName || '', props, 'backward');
    },
    [stepIndex, steps, setStep],
  );

  const ActiveStep: React.FC = () => {
    const { component: StepComponent } = steps[stepIndex];
    const C = StepComponent as React.FC<any>;
    return (
      <C
        totalSteps={steps.length}
        {...stepProps}
        setStep={setStep}
        nextStep={nextStep}
        previousStep={previousStep}
      />
    );
  };

  return {
    activeStepContext: {
      stepName: steps[stepIndex].stepName,
      component: ActiveStep,
      direction: directionRef.current,
      config: steps[stepIndex].config,
    },
    setStep,
    nextStep,
    previousStep,
    onComplete: options?.onComplete,
  };
}
